import { type LocaleDefinition } from '@solinkcorp/locale';
import { getLocalTimeZone } from '@solinkcorp/utils';
import moment from 'moment-timezone';
import PreferenceStore from 'stores/PreferenceStore';
import { TIME_FORMAT, type TimeFormat } from 'vd-constants';

class TimeFormatter {
  /**
   * @description Determine whether to use 12/24 hour clock based on the User's Preferences.
   */
  is24HrClock(overrideTimeFormat?: TimeFormat) {
    const timeFormat = overrideTimeFormat || PreferenceStore.getTimeFormat();
    switch (timeFormat) {
      case TIME_FORMAT.TWENTY_FOUR_HOUR:
        return true;
      case TIME_FORMAT.TWELVE_HOUR:
      default:
        return false;
    }
  }

  getString(
    options: {
      zeroPrefix?: boolean;
      minutes?: boolean;
      seconds?: boolean;
      timeFormat?: TimeFormat;
      showTimezone?: boolean;
    } = {},
  ) {
    const { zeroPrefix = true, minutes = true, seconds = true, showTimezone = true } = options;

    const timezoneFormat = showTimezone ? ' z' : '';

    if (this.is24HrClock(options.timeFormat)) {
      if (seconds) {
        return 'HH:mm:ss' + timezoneFormat;
      }
      return 'HH:mm' + timezoneFormat;
    }
    if (!!zeroPrefix && !minutes && !seconds) {
      return 'hA' + timezoneFormat;
    } else if (!!zeroPrefix && !!minutes && !!seconds) {
      return 'hh:mm:ss A' + timezoneFormat;
    } else if (!!zeroPrefix && !!minutes && !seconds) {
      return 'hh:mm A' + timezoneFormat;
    } else if (!zeroPrefix && !!minutes && !!seconds) {
      return 'h:mm:ss A' + timezoneFormat;
    } else if (!zeroPrefix && !!minutes && !seconds) {
      return 'h:mm A' + timezoneFormat;
    }
    return 'hh:mm:ss A' + timezoneFormat;
  }

  getTimeString({
    time,
    timeZoneId,
    timeFormat,
  }: {
    time: Date | number;
    timeZoneId: string;
    timeFormat?: TimeFormat;
  }) {
    return moment(time)
      .clone()
      .tz(timeZoneId)
      .format(this.is24HrClock(timeFormat) ? 'HH:mm' : 'h:mm A');
  }

  getDateTime(
    options: {
      zeroPrefix?: boolean;
      minutes?: boolean;
      seconds?: boolean;
      timeFormat?: TimeFormat;
      timezoneId?: string;
      showTimezone?: boolean;
    },
    time: string | Date,
  ) {
    const { timezoneId } = options;
    const timeFormat = this.getString(options);
    const dateFormat = 'LL';
    const dateTimeFormat = `${dateFormat} ${timeFormat}`;
    return moment(time)
      .clone()
      .tz(timezoneId || getLocalTimeZone())
      .format(dateTimeFormat);
  }

  getStartEndTimeString({
    startTime,
    endTime,
    locale,
    timezoneId,
  }: {
    startTime: Date | number;
    endTime: Date | number;
    locale: LocaleDefinition;
    timezoneId: string;
  }) {
    const startTimeStringFormat = this.getString({
      zeroPrefix: false,
      seconds: false,
      showTimezone: false,
    });
    const timeStringFormat = this.getString({ zeroPrefix: false, seconds: false });
    const fullDateTimeFormat = `${locale.date.format1} ${startTimeStringFormat}`;
    const dateTimeFormat = `${locale.date.monthAndDay} ${timeStringFormat}`;
    const isEndSameDay = moment(endTime).isSame(startTime, 'day');
    const isEndSameYear = moment(endTime).isSame(startTime, 'year');

    const startTimeString = moment(startTime).tz(timezoneId).format(fullDateTimeFormat);
    let endTimeString;
    const endTimeInTimezone = moment(endTime).tz(timezoneId);
    if (isEndSameDay) {
      endTimeString = endTimeInTimezone.format(timeStringFormat);
    } else if (!isEndSameYear) {
      endTimeString = endTimeInTimezone.format(fullDateTimeFormat);
    } else {
      endTimeString = endTimeInTimezone.format(dateTimeFormat);
    }

    return {
      fullString: `${startTimeString} - ${endTimeString}`,
      startTimeString,
      endTimeString,
    };
  }
}

export default new TimeFormatter();
